import React from "react"
import { ShortChatBotFlowBlock, ShortChatBotTrigger, ShortFlow } from "@/protocols/chatBotConstructor"
import { BlockErrorContent, ChatBotFlowError, FlowError } from "@/protocols/chatBotFlow"
import { ChatBotFlowBlockRule, ChatBotFlowBlockRuleValidation, ChatBotFlowBlockType, ChatBotFlowTriggerType, ManageContactInBoardActionToManageContactInBoardData } from "@/protocols/chatBot"

import useChatBotFlowConstructorStore from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowConstructorStore"
import { getConsolidatedBlockTheme } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/blockTheme"

import { useReactFlow } from "reactflow"
import { MINUTE_IN_MS } from "@/utils/time"
import { getAIConditionMatchDefaultValidation, getConditionByValidationSlug } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockRule"

import { PlayArrow as TriggerNodeIcon } from "@material-ui/icons"

import { colors } from "@/styles/colors"
import HardCodedService from "@/services/HardCoded"

type AIConditionValidationData = NonNullable<ChatBotFlowBlockRuleValidation["extra_data"]>["ai_condition"]

type UseChatBotFlowValidationResponse = {
	validateFlow: (chatBotFlow: ShortFlow) => FlowError
	blockAlreadyExistInFlow: (blockType: ChatBotFlowBlockType, chatBotFlow: ShortFlow) => boolean
	triggerAlreadyExistInFlow: (chatBotFlowTriggers: ShortFlow["chat_bot_triggers"], chatBotFlowTrigger: ChatBotFlowTriggerType) => boolean
}

type BlockValidator = (block: ShortChatBotFlowBlock) => {
	errorType?: ChatBotFlowError
} | null

const useChatBotFlowValidation = (): UseChatBotFlowValidationResponse => {
	const chatBotFlowConstructorStore = useChatBotFlowConstructorStore()

	const reactFlow = useReactFlow()

	const filterChatBotFlowByStatus = (chatBotFlow: ShortFlow, status: ShortChatBotFlowBlock["status"]) => {
		const newChatBotFlowBlocks = chatBotFlow.chat_bot_flow_blocks.filter(block => block.status !== status)

		const filteredChatBotFlow: ShortFlow = {
			...chatBotFlow,
			chat_bot_flow_blocks: [
				...newChatBotFlowBlocks
			]
		}

		return filteredChatBotFlow
	}

	const filterChatBotFlowTriggerByStatus = (chatBotFlowTriggers: ShortFlow["chat_bot_triggers"], status: ShortChatBotTrigger["status"]) => {
		const filteredChatBotFlowTriggers = chatBotFlowTriggers.filter(trigger => trigger.status !== status)

		return filteredChatBotFlowTriggers
	}

	const getConsolidatedBlockByBlockType = (blockType: ChatBotFlowBlockType) => {
		const blockCategoryType = chatBotFlowConstructorStore.getBlockCategoryType(blockType)
		return getConsolidatedBlockTheme(blockType, blockCategoryType)
	}

	const finishFlowBlockAlreadyExist = (chatBotFlow: ShortFlow): BlockErrorContent | undefined => {
		const finishFlowBlock = chatBotFlow.chat_bot_flow_blocks?.find(block => block.chat_bot_block_type === "finish-flow")

		if (finishFlowBlock) {
			return
		}

		const consolidatedBlock = getConsolidatedBlockByBlockType("finish-flow")

		return {
			errors: ["finish_flow_block_not_find"],
			block: {
				icon: consolidatedBlock.icon,
				color: consolidatedBlock.color,
				name: "Finalizar bot"
			},
			type: "flow_error"
		}
	}

	const blockAlreadyExistInFlow = (blockType: ChatBotFlowBlockType, chatBotFlow: ShortFlow): boolean => {
		const filteredChatBotFlow = filterChatBotFlowByStatus(chatBotFlow, "DELETED")

		const block = filteredChatBotFlow.chat_bot_flow_blocks?.find(block => block.chat_bot_block_type === blockType)

		return !!block
	}

	const triggerAlreadyExistInFlow = (chatBotFlowTriggers: ShortFlow["chat_bot_triggers"], chatBotFlowTrigger: ChatBotFlowTriggerType): boolean => {
		const filteredChatBotFlowTriggers = filterChatBotFlowTriggerByStatus(chatBotFlowTriggers, "DELETED")

		const trigger = filteredChatBotFlowTriggers?.find(trigger => trigger.type === chatBotFlowTrigger)

		return !!trigger
	}

	const getNodeByChatBotFlowBlockId = (chatBotFlowBlockId: number) => {
		const nodes = reactFlow.getNodes()
		return nodes.find(node => node.data.chatBotFlowBlockId === chatBotFlowBlockId)
	}

	const isMissingAnyNextConnectionInFlowBlock = (nodeId: string, rulesConnections: number): boolean => {
		const connections = reactFlow.getEdges()

		const nextConnections = connections.filter(connection => connection.source === nodeId)
		return nextConnections.length !== rulesConnections
	}

	const getExpectedNextConnectionsCount = (block: ShortChatBotFlowBlock): number => {
		const getBoardCardFieldExpectedConnections = (block: ShortChatBotFlowBlock): number => {
			const isNotInAIMode = block.content?.boardCardFieldData?.mode !== "ia"

			if (!isNotInAIMode) {
				return block.next_chat_bot_flow_block_rules.length
			}

			const fallbackCondition = getConditionByValidationSlug("contact-value-extraction-found-value", block.next_chat_bot_flow_block_rules)

			if (!fallbackCondition) {
				return block.next_chat_bot_flow_block_rules.length
			}

			return block.next_chat_bot_flow_block_rules.length - 1
		}

		const getGenericExpectedConnections = (block: ShortChatBotFlowBlock): number => {
			return block.next_chat_bot_flow_block_rules.length
		}

		const blockTypeToExpectedConnectionsFn: Record<ChatBotFlowBlockType, (block: ShortChatBotFlowBlock) => number> = {
			"conditional-text": getGenericExpectedConnections,
			"finish-attendance": getGenericExpectedConnections,
			"assign-attendance": getGenericExpectedConnections,
			"send-message": getGenericExpectedConnections,
			"assign-attendance-randomly": getGenericExpectedConnections,
			"assign-attendance-to-account-manager": getGenericExpectedConnections,
			"assign-attendance-to-general-queue": getGenericExpectedConnections,
			"restart-flow": getGenericExpectedConnections,
			"attendance-schedule": getGenericExpectedConnections,
			"conditional-button": getGenericExpectedConnections,
			"conditional-menu": getGenericExpectedConnections,
			"menu-list": getGenericExpectedConnections,
			"manage-client-tags": getGenericExpectedConnections,
			"save-input": getGenericExpectedConnections,
			"go-to-flow": getGenericExpectedConnections,
			"send-webhook": getGenericExpectedConnections,
			"finish-flow": getGenericExpectedConnections,
			"register-contact-to-active-campaign": getGenericExpectedConnections,
			"attendance-transfer": getGenericExpectedConnections,
			"register-deal-to-active-campaign": getGenericExpectedConnections,
			repeat: getGenericExpectedConnections,
			"optout-optin": getGenericExpectedConnections,
			wait: getGenericExpectedConnections,
			condition: getGenericExpectedConnections,
			"ask-and-wait": getGenericExpectedConnections,
			"validate-and-save": getGenericExpectedConnections,
			"random-path-flow": getGenericExpectedConnections,
			"api-block": getGenericExpectedConnections,
			"ai-agent-api": getGenericExpectedConnections,
			"register-tag-to-active-campaign": getGenericExpectedConnections,
			"meta-ads-conversion-event": getGenericExpectedConnections,
			"validate-and-save-v2": getGenericExpectedConnections,
			"send-waba-template-message-and-wait": getGenericExpectedConnections,
			"conditional-waba-button-template": getGenericExpectedConnections,
			"manage-contact-in-board": getGenericExpectedConnections,
			"ai-condition": getGenericExpectedConnections,
			"ai-intention": getGenericExpectedConnections,
			"ai-agent": getGenericExpectedConnections,
			"ai-follow-up": getGenericExpectedConnections,
			"ai-manage-client-tags": getGenericExpectedConnections,
			"custom-field-fill": getGenericExpectedConnections,
			"wait-v2": getGenericExpectedConnections,
			"board-card-field": getBoardCardFieldExpectedConnections,
			"create-rd-station-contact": getGenericExpectedConnections,
			"create-rd-station-deal": getGenericExpectedConnections,
			"update-rd-station-deal": getGenericExpectedConnections,
			"get-superlogica-contact-info": getGenericExpectedConnections
		}

		const getExpectedConnections = blockTypeToExpectedConnectionsFn[block.chat_bot_block_type]

		return getExpectedConnections(block)
	}

	const isMissingAnyPreviousConnectionInFlowBlock = (nodeId: string): boolean => {
		const connections = reactFlow.getEdges()

		const previousConnection = connections.find(connection => connection.target === nodeId)

		return !previousConnection
	}

	const hasContentToSend = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		if (block?.content?.messagesToSend?.length > 0) {
			return null
		}
		return {
			errorType: "block_missing_content"
		}
	}

	const hasMenuListRequiredContent = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		const hasBody = Boolean(block?.content?.menuListData?.body)
		const hasActionButton = Boolean(block?.content?.menuListData?.actionButton)

		if (hasBody && hasActionButton) {
			return null
		}

		return {
			errorType: "block_missing_content"
		}
	}

	const hasAttendanceTransferData = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		const transferData = block?.content?.attendanceTransferData
		if (transferData) {
			const specificTypes = ["random-team-attendant", "specific-attendant", "specific-team"]
			const hasSpecificTypes = specificTypes.includes(transferData?.type)
			const hasSelection = transferData?.teamId !== null || transferData?.userId !== null

			if (hasSpecificTypes && !hasSelection) {
				return {
					errorType: "transfer_block_missing_selection"
				}
			}
			return null
		}
		return {
			errorType: "transfer_block_missing_selection"
		}
	}

	const hasAtLeatOneAnswerForConditionalBlock = (rules: ChatBotFlowBlockRule[]): {errorType: ChatBotFlowError} | null => {
		if (rules.some(rule => rule.category === "continue-condition")) {
			return null
		}
		return {
			errorType: "multiple_condition_block_without_answer"
		}
	}

	const hasAttendanceSchedule = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		const { next_chat_bot_flow_block_rules } = block
		const hasContinueCondition = next_chat_bot_flow_block_rules.some(rule => rule.category === "continue-condition" && rule.validations.length > 0)

		if (hasContinueCondition) {
			return null
		}

		return {
			errorType: "attendace_schedule_missing_time"
		}
	}

	const validateWeekdayTimeRange = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		const { next_chat_bot_flow_block_rules } = block

		const continueConditionRule = next_chat_bot_flow_block_rules.filter(
			rule => rule.category === "continue-condition"
		)

		for (const rule of continueConditionRule) {
			const timeRangeValidation = rule?.validations.filter(
				validation => validation.slug === "between-week-time"
			)

			for (const timeRange of timeRangeValidation) {
				const startTimeRaw = timeRange?.deep_validations?.find(
					validation => validation.slug === "from-hour-and-minutes"
				)?.second_param as string

				const endTimeRaw = timeRange?.deep_validations?.find(
					validation => validation.slug === "to-hour-and-minutes"
				)?.second_param as string

				const [startHourStr, startMinuteStr] = startTimeRaw?.split("\n")?.[0]?.split(":") || []
				const [endHourStr, endMinuteStr] = endTimeRaw?.split("\n")?.[0]?.split(":") || []

				const startHour = Number(startHourStr)
				const startMinute = Number(startMinuteStr)
				const endHour = Number(endHourStr)
				const endMinute = Number(endMinuteStr)

				const startTotalMinutes = (startHour || 0) * MINUTE_IN_MS + (startMinute || 0)
				const endTotalMinutes = (endHour || 0) * MINUTE_IN_MS + (endMinute || 0)

				if (endTotalMinutes <= startTotalMinutes) {
					return {
						errorType: "attendance_schedule_end_time_cannot_be_equal_or_lower_than_start_time"
					}
				}
			}
		}

		return null
	}

	const hasLocationToSaveAnswer = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		const locationToSave = block?.content?.locationToSaveInput
		if (locationToSave && Object.keys(locationToSave).length > 0) {
			return null
		}
		return {
			errorType: "save_answer_block_missing_location"
		}
	}

	const hasVariableNameForWaitForClientResponse = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		if (!HardCodedService.checkFeatureFlag("canValidateWaitForClientResponseBlockVariableName")) {
			return null
		}

		if (!block?.content?.variableName) {
			return {
				errorType: "wait_for_client_response_block_missing_variable_name"
			}
		}

		return null
	}

	const hasGoToBot = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		if (block?.content?.goToChatBotFlowId) {
			return null
		}
		return {
			errorType: "go_to_block_missing_bot"
		}
	}

	const hasTagSelected = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		const hasTagSelected = block?.content?.clientTagsManagementData
		if (hasTagSelected && Object.keys(hasTagSelected?.actions).length > 0) {
			return null
		}
		return {
			errorType: "manage_client_tags_block_missing_tag_selection"
		}
	}

	const hasCustomFieldSelected = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		const hasCustomFieldSelected = block?.content?.customFieldFillData?.fields
		if (hasCustomFieldSelected && hasCustomFieldSelected.length > 0) {
			return null
		}
		return {
			errorType: "custom_field_fill_block_missing_field_selection"
		}
	}

	const hasCustomFieldsWithInstruction = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		const fields = block?.content?.customFieldFillData?.fields ?? []

		const hasFieldWithoutInstruction = fields.some(field => (field.instruction ?? "").trim() === "")

		if (hasFieldWithoutInstruction) {
			return {
				errorType: "custom_field_fill_block_missing_instruction"
			}
		}

		return null
	}

	const hasTagsWithDescription = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		const actions = block?.content?.clientTagsManagementData?.actions ?? []
		const tagOptions = chatBotFlowConstructorStore.constructionResources.tagOptions ?? []

		const hasTagWithoutDescription = actions.some(action => {
			const tagOption = tagOptions.find(option => option.id === action.tagId)
			const description = tagOption?.description ?? ""
			return description.trim() === ""
		})

		if (hasTagWithoutDescription) {
			return {
				errorType: "ai_manage_client_tags_block_missing_description"
			}
		}

		return null
	}

	const hasPathAdded = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		if (Object.keys(block?.next_chat_bot_flow_block_rules).length > 0) {
			return null
		}
		return {
			errorType: "random_path_flow_missing_path"
		}
	}

	const hasOptoutOptinOptionSelected = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		if (block?.content?.autoSendMessagesOption) {
			return null
		}
		return {
			errorType: "optout_optin_missing_selection"
		}
	}

	const hasUrlAdded = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		if (block?.content?.webhook?.url.trim() !== "") {
			return null
		}
		return {
			errorType: "webhook_block_missing_url"
		}
	}

	const hasCondition = (block: ShortChatBotFlowBlock) : {errorType: ChatBotFlowError} | null => {
		if (block.next_chat_bot_flow_block_rules.length <= 1) {
			return { errorType: "condition_block_missing_option" }
		}

		const hasCondition = block.next_chat_bot_flow_block_rules.some((nextChatBotFlow: ChatBotFlowBlockRule) =>
			nextChatBotFlow.category !== "exit-condition" && nextChatBotFlow.validations.length >= 1
		)

		return hasCondition ? null : { errorType: "condition_block_missing_option" }
	}

	const hasTagAddedToActiveCampaign = (block: ShortChatBotFlowBlock) : {errorType: ChatBotFlowError} | null => {
		if (block?.content?.activeCampaignTagName && block?.content?.activeCampaignTagName !== "") {
			return null
		}

		return {
			errorType: "active_campaign_missing_tag"
		}
	}

	const hasEventConversation = (block: ShortChatBotFlowBlock) : {errorType: ChatBotFlowError} | null => {
		if (block?.content?.metaAdsConversionEvent?.name) {
			return null
		}

		return {
			errorType: "meta_ads_event_conversion_missing"
		}
	}

	const hasApiUrlAdded = (block: ShortChatBotFlowBlock) : {errorType: ChatBotFlowError} | null => {
		if (block?.content?.apiBlock?.url) {
			return null
		}

		return {
			errorType: "api_block_missing_url"
		}
	}

	const hasCustomFieldConfigurated = (block: ShortChatBotFlowBlock): { errorType: ChatBotFlowError } | null => {
		const associations = block?.content?.apiBlock?.customFieldWebhookAssociation

		if (!associations || !Array.isArray(associations) || associations.length === 0) {
			return {
				errorType: "api_block_custom_field"
			}
		}

		const hasInvalidField = associations.some(assoc => {
			return !assoc.customFieldName || !assoc.webhookField
		})

		if (hasInvalidField) {
			return {
				errorType: "api_block_custom_field"
			}
		}

		return null
	}

	const hasSuperlogicaCondominiumConfigured = (block: ShortChatBotFlowBlock): { errorType: ChatBotFlowError } | null => {
		const search = block?.content?.superlogicaBlock?.search || []
		const hasCondominiumConfigured = search.some(item => item.field === "condominiumName" && item.customFieldId)

		if (hasCondominiumConfigured) {
			return null
		}

		return {
			errorType: "superlogica_block_missing_condominium"
		}
	}

	const hasSuperlogicaAssociationConfigured = (block: ShortChatBotFlowBlock): { errorType: ChatBotFlowError } | null => {
		const responseMapping = block?.content?.superlogicaBlock?.responseMapping || []
		const hasValidMapping = responseMapping.some(item => item.customFieldId)

		if (hasValidMapping) {
			return null
		}

		return {
			errorType: "superlogica_block_missing_association"
		}
	}

	const hasDealRegisteredToActiveCampaign = (block: ShortChatBotFlowBlock) : {errorType: ChatBotFlowError} | null => {
		if (block?.content?.deal?.pipeline && block?.content?.deal?.stage) {
			return null
		}

		return {
			errorType: "active_campaign_block_missing_deal"
		}
	}

	const hasContentToManagerContactInBoard = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		const currentActionInManagerContactInBoard = block?.content?.manageContactInBoardData?.action

		if (currentActionInManagerContactInBoard) {
			return null
		}

		return {
			errorType: "missing_action_in_manager_contact_in_board"
		}
	}

	const hasSelectedBoardToManagerContactInBoard = (block: ShortChatBotFlowBlock): { errorType: ChatBotFlowError } | null => {
		const currentBoardInManagerContactInBoard = block?.content?.manageContactInBoardData?.data?.board?.id

		if (currentBoardInManagerContactInBoard) {
			return null
		}

		return {
			errorType: "missing_selected_board_in_manager_contact_in_board"
		}
	}

	const hasSelectedStageToManagerContactInBoard = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		const currentManagerContactInBoardAction = block?.content?.manageContactInBoardData?.action

		if (!currentManagerContactInBoardAction) {
			return null
		}

		if (currentManagerContactInBoardAction === "add-or-move-contact-to-board-stage") {
			const managerContactInBoardData = block?.content?.manageContactInBoardData?.data as ManageContactInBoardActionToManageContactInBoardData["add-or-move-contact-to-board-stage"]
			const currentManagerContactInBoardAction = managerContactInBoardData?.stage?.id

			if (currentManagerContactInBoardAction) {
				return null
			}

			return {
				errorType: "missing_selected_stage_in_manager_contact_in_board"
			}
		} else if (currentManagerContactInBoardAction === "remove-contact-from-board") {
			return null
		}

		return null
	}

	const checkIfIsThereAnyMissingAIConditionParam = (rules: ChatBotFlowBlockRule[], paramName: keyof AIConditionValidationData): boolean => {
		return rules.some(rule => {
			const aiConditionMatchDefaultValidation = getAIConditionMatchDefaultValidation(rule)

			if (!aiConditionMatchDefaultValidation) {
				return false
			}

			const conditionParam = aiConditionMatchDefaultValidation?.extra_data?.ai_condition?.[paramName]
			const isMissingConditionParam = !conditionParam

			if (isMissingConditionParam) {
				return true
			}

			return false
		})
	}

	const hasAddedRequiredParamToAIConditionBlock = (block: ShortChatBotFlowBlock, paramName: keyof AIConditionValidationData): { errorType: ChatBotFlowError } | null => {
		const isMissingRequiredParam = checkIfIsThereAnyMissingAIConditionParam(block.next_chat_bot_flow_block_rules, paramName)

		if (isMissingRequiredParam) {
			const PARAM_NAME_TO_ERROR_TYPE: Record<keyof AIConditionValidationData, ChatBotFlowError> = {
				title: "missing_condition_title_in_ai_condition_block",
				description: "missing_condition_description_in_ai_condition_block",
				example: "missing_condition_example_in_ai_condition_block"
			}

			return {
				errorType: PARAM_NAME_TO_ERROR_TYPE[paramName]
			}
		}

		return null
	}

	const hasAddedRequiredParamToAIIntentionBlock = (block: ShortChatBotFlowBlock, paramName: keyof AIConditionValidationData): { errorType: ChatBotFlowError } | null => {
		const isMissingRequiredParam = checkIfIsThereAnyMissingAIConditionParam(block.next_chat_bot_flow_block_rules, paramName)

		if (isMissingRequiredParam) {
			const PARAM_NAME_TO_ERROR_TYPE: Record<keyof AIConditionValidationData, ChatBotFlowError> = {
				title: "missing_condition_title_in_ai_intention_block",
				description: "missing_condition_description_in_ai_intention_block",
				example: "missing_condition_example_in_ai_intention_block"
			}

			return {
				errorType: PARAM_NAME_TO_ERROR_TYPE[paramName]
			}
		}

		return null
	}

	const hasAgentSelected = (block: ShortChatBotFlowBlock): {errorType: ChatBotFlowError} | null => {
		if (block?.content?.agent?.id) {
			return null
		}

		return {
			errorType: "ai_agent_block_missing_agent"
		}
	}

	const hasDealContentToCreateDealRDStationCRM = (block: ShortChatBotFlowBlock) : {errorType: ChatBotFlowError} | null => {
		if (block?.content?.rdStationDeal?.pipeline && block?.content?.rdStationDeal?.stage && block?.content?.rdStationDeal?.name) {
			return null
		}

		return {
			errorType: "rd_station_crm_block_missing_deal"
		}
	}

	const hasDealContentToUpdateDealRDStationCRM = (block: ShortChatBotFlowBlock) : {errorType: ChatBotFlowError} | null => {
		if (block?.content?.rdStationDealUpdate?.pipeline && block?.content?.rdStationDealUpdate?.stage && block?.content?.rdStationDealUpdate?.status) {
			return null
		}

		return {
			errorType: "rd_station_crm_block_missing_deal"
		}
	}

	const hasAtLeastOneAttempt = (block: ShortChatBotFlowBlock): { errorType: ChatBotFlowError } | null => {
		const attempts = block?.content?.aiFollowUpData?.attempts || []

		if (attempts.length === 0) {
			return {
				errorType: "ai_follow_up_block_missing_attempt"
			}
		}

		return null
	}

	const hasFallbackTemplateWhenRequired = (block: ShortChatBotFlowBlock): { errorType: ChatBotFlowError } | null => {
		const flowChannelType = chatBotFlowConstructorStore.flowData.inbox_channel_type

		if (flowChannelType !== "waba") {
			return null
		}

		const attempts = block?.content?.aiFollowUpData?.attempts || []
		const hasAttemptWithoutFallbackTemplate = attempts.some(attempt => !attempt.wabaTemplate?.id)

		if (hasAttemptWithoutFallbackTemplate) {
			return {
				errorType: "ai_follow_up_block_missing_fallback_template"
			}
		}

		return null
	}

	const blockTypeToBlockValidator = (key: ChatBotFlowBlockType): BlockValidator[] => {
		const validators: Record<ChatBotFlowBlockType, BlockValidator[]> = {
			"send-message": [
				(block) => hasContentToSend(block)
			],
			"ask-and-wait": [
				(block) => hasContentToSend(block),
				(block) => hasVariableNameForWaitForClientResponse(block)
			],
			"conditional-text": [
				(block) => hasContentToSend(block),
				(block) => hasAtLeatOneAnswerForConditionalBlock(block.next_chat_bot_flow_block_rules),
				(block) => hasVariableNameForWaitForClientResponse(block)
			],
			"attendance-schedule": [
				(block) => hasAttendanceSchedule(block),
				(block) => validateWeekdayTimeRange(block)
			],
			"attendance-transfer": [
				(block) => hasAttendanceTransferData(block)
			],
			"validate-and-save-v2": [
				(block) => hasLocationToSaveAnswer(block)
			],
			"manage-client-tags": [
				(block) => hasTagSelected(block)
			],
			"go-to-flow": [
				(block) => hasGoToBot(block)
			],
			"random-path-flow": [
				(block) => hasPathAdded(block)
			],
			"optout-optin": [
				(block) => hasOptoutOptinOptionSelected(block)
			],
			"send-webhook": [
				(block) => hasUrlAdded(block)
			],
			condition: [
				(block) => hasCondition(block)
			],
			"register-contact-to-active-campaign": [],
			"register-tag-to-active-campaign": [
				(block) => hasTagAddedToActiveCampaign(block)
			],
			"meta-ads-conversion-event": [
				(block) => hasEventConversation(block)
			],
			"register-deal-to-active-campaign": [
				(block) => hasDealRegisteredToActiveCampaign(block)
			],
			"api-block": [
				(block) => hasApiUrlAdded(block),
				(block) => hasCustomFieldConfigurated(block)
			],
			"finish-attendance": [],
			"assign-attendance": [],
			"assign-attendance-randomly": [],
			"assign-attendance-to-account-manager": [],
			"assign-attendance-to-general-queue": [],
			"restart-flow": [],
			"conditional-button": [
				(block) => hasVariableNameForWaitForClientResponse(block)
			],
			"conditional-menu": [
				(block) => hasVariableNameForWaitForClientResponse(block)
			],
			"menu-list": [
				(block) => hasMenuListRequiredContent(block),
				(block) => hasVariableNameForWaitForClientResponse(block)
			],
			"save-input": [
				(block) => hasVariableNameForWaitForClientResponse(block)
			],
			"finish-flow": [],
			repeat: [],
			wait: [],
			"validate-and-save": [],
			"ai-agent-api": [],
			"send-waba-template-message-and-wait": [
				(block) => hasVariableNameForWaitForClientResponse(block)
			],
			"conditional-waba-button-template": [
				(block) => hasVariableNameForWaitForClientResponse(block)
			],
			"manage-contact-in-board": [
				(block) => hasContentToManagerContactInBoard(block),
				(block) => hasSelectedBoardToManagerContactInBoard(block),
				(block) => hasSelectedStageToManagerContactInBoard(block)
			],
			"ai-condition": [
				(block) => hasAddedRequiredParamToAIConditionBlock(block, "title"),
				(block) => hasAddedRequiredParamToAIConditionBlock(block, "description"),
				(block) => hasAddedRequiredParamToAIConditionBlock(block, "example")
			],
			"ai-intention": [
				(block) => hasAddedRequiredParamToAIIntentionBlock(block, "title"),
				(block) => hasAddedRequiredParamToAIIntentionBlock(block, "description"),
				(block) => hasAddedRequiredParamToAIIntentionBlock(block, "example")
			],
			"ai-agent": [
				(block) => hasAgentSelected(block)
			],
			"wait-v2": [],
			"create-rd-station-contact": [],
			"create-rd-station-deal": [
				(block) => hasDealContentToCreateDealRDStationCRM(block)
			],
			"update-rd-station-deal": [
				(block) => hasDealContentToUpdateDealRDStationCRM(block)
			],
			"ai-follow-up": [
				(block) => hasAtLeastOneAttempt(block),
				(block) => hasFallbackTemplateWhenRequired(block)
			],
			"get-superlogica-contact-info": [
				(block) => hasSuperlogicaCondominiumConfigured(block),
				(block) => hasSuperlogicaAssociationConfigured(block)
			],
			"ai-manage-client-tags": [
				(block) => hasTagSelected(block),
				(block) => hasTagsWithDescription(block)
			],
			"custom-field-fill": [
				(block) => hasCustomFieldSelected(block),
				(block) => hasCustomFieldsWithInstruction(block)
			]
		}

		return validators[key] || null
	}

	const validateBlock = (block: ShortChatBotFlowBlock): BlockErrorContent | void => {
		const blockErrors: ChatBotFlowError[] = []

		const node = getNodeByChatBotFlowBlockId(block.id)

		if (!node) return

		let isMissingAnyNextConnectionInFlowBlockResult = false

		const isMissingAnyPreviousConnectionInFlowBlockResult = isMissingAnyPreviousConnectionInFlowBlock(node.id)

		if (block.chat_bot_block_type !== "finish-flow") {
			isMissingAnyNextConnectionInFlowBlockResult = isMissingAnyNextConnectionInFlowBlock(node.id, getExpectedNextConnectionsCount(block))
		}

		if (isMissingAnyPreviousConnectionInFlowBlockResult || isMissingAnyNextConnectionInFlowBlockResult) {
			blockErrors.push("block_without_mandatory_connection")
		}

		const validators = blockTypeToBlockValidator(block.chat_bot_block_type)
		if (validators) {
			validators.forEach((validator) => {
				const result = validator(block)
				if (result?.errorType) {
					blockErrors.push(result.errorType)
				}
			})
		}

		if (blockErrors.length > 0) {
			const consolidatedBlock = getConsolidatedBlockByBlockType(block.chat_bot_block_type)
			const node = getNodeByChatBotFlowBlockId(block.id)

			return {
				block: {
					nodeId: node?.id,
					icon: consolidatedBlock.icon,
					color: consolidatedBlock.color,
					name: node?.data.flowBlock.title
				},
				errors: blockErrors,
				type: "block_error"
			}
		}
	}

	const validateBlocks = (chatBotFlowBlocks: ShortChatBotFlowBlock[]): FlowError | void => {
		const blockErrors: FlowError = []

		chatBotFlowBlocks.forEach(block => {
			const validateBlockResult = validateBlock(block)

			if (validateBlockResult) {
				blockErrors.push(validateBlockResult)
			}
		})

		if (blockErrors.length > 0) {
			return blockErrors
		}
	}

	const validateTrigger = (): BlockErrorContent | undefined => {
		const nodes = reactFlow.getNodes()
		const triggerNode = nodes.find(node => node.type === "triggernode")

		const connections = reactFlow.getEdges()

		const nextConnections = connections.filter(connection => connection.source === triggerNode?.id)

		if (nextConnections.length > 0) {
			return
		}

		return {
			errors: ["block_without_mandatory_connection"],
			block: {
				icon: {
					Component: <TriggerNodeIcon />
				},
				color: {
					blockIcon: colors.getPrimaryColor(),
					blockHeaderBackground: colors.purple[50],
					blockSelected: colors.yellow[400]
				},
				name: "Gatilho para acionar o bot",
				nodeId: triggerNode?.id
			},
			type: "block_error"
		}
	}

	const validateFlow = (chatBotFlow: ShortFlow): FlowError => {
		const flowErrors: FlowError = []

		const filteredChatBotFlow = filterChatBotFlowByStatus(chatBotFlow, "DELETED")

		const isFinishFlowBlockError = finishFlowBlockAlreadyExist(filteredChatBotFlow)

		if (isFinishFlowBlockError) {
			flowErrors.push(isFinishFlowBlockError)
		}

		const blockErrors = validateBlocks(filteredChatBotFlow.chat_bot_flow_blocks)

		if (blockErrors) {
			flowErrors.push(...blockErrors)
		}

		const triggerErrors = validateTrigger()

		if (triggerErrors) {
			flowErrors.push(triggerErrors)
		}

		chatBotFlowConstructorStore.setValidationErrorsCount(flowErrors.length)

		return flowErrors
	}

	return {
		validateFlow,
		blockAlreadyExistInFlow,
		triggerAlreadyExistInFlow
	}
}

export default useChatBotFlowValidation
